import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Card from '@mui/material/Card';
import TextField from '@mui/material/TextField';
import InputAdornment from '@mui/material/InputAdornment';
import TableSortLabel from '@mui/material/TableSortLabel';
import TablePagination from '@mui/material/TablePagination';
import { styled } from '@mui/material/styles';
import { toast } from 'react-toastify';
import api from '../services/api';
import Chip from '@mui/material/Chip';
import { Search, Eye, X, FileText, TrendingUp, User, Users as CustomersLogoIcon, ShieldCheck, Mail } from 'lucide-react';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Button from '@mui/material/Button';
import Alert from '@mui/material/Alert';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import Grid from '@mui/material/Grid';
import IconButton from '@mui/material/IconButton';
import CircularProgress from '@mui/material/CircularProgress';
import Badge from '@mui/material/Badge';
import FilterAltIcon from '@mui/icons-material/FilterAlt';
import Tooltip from '@mui/material/Tooltip';
import LoaderProgress from '../components/ui/loader-progress';
import { useJob } from '../context/JobContext';
import { useAuth } from '../context/AuthContext';
import { Download as DownloadIcon } from 'lucide-react';
import Autocomplete from '@mui/material/Autocomplete';
import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';
import Menu from '@mui/material/Menu';
import ViewColumnIcon from '@mui/icons-material/ViewColumn';

const StyledCard = styled(Card)(({ theme }) => ({
  boxShadow: '0 0 2px 0 rgba(145, 158, 171, 0.2), 0 12px 24px -4px rgba(145, 158, 171, 0.12)',
  borderRadius: 16,
  overflow: 'hidden',
}));

const CUSTOMER_COLUMN_DEFS = [
  { key: 'id', label: 'ID' },
  { key: 'stato_comunicazione', label: 'Stato' },
  { key: 'surname', label: 'Cognome' },
  { key: 'name', label: 'Nome' },
  { key: 'gender', label: 'Sesso' },
  { key: 'data_nascita', label: 'Data Nascita' },
  { key: 'codice_fiscale', label: 'Codice Fiscale' },
  { key: 'email', label: 'Email' },
  { key: 'telephone', label: 'Telefono' }
];
const DEFAULT_VISIBLE_CUSTOMER_COLUMNS = CUSTOMER_COLUMN_DEFS.filter((column) => column.key !== 'id').map((column) => column.key);

export default function Customer() {
  const navigate = useNavigate();
  const { addJob } = useJob();
  const { user } = useAuth();
  const roleName = String(user?.role_name || user?.role || '').toLowerCase();
  const departmentName = String(user?.department_name || '').toLowerCase();
  const isAdmin = roleName === 'admin';
  const isAmministrazione = departmentName.includes('amministrazione') || departmentName.includes('administration');
  const canAssignClienti = isAdmin || isAmministrazione;
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [importingPfClienti, setImportingPfClienti] = useState(false);
  const [order, setOrder] = useState('asc');
  const [orderBy, setOrderBy] = useState('id');
  const [filterName, setFilterName] = useState('');
  const [debouncedFilterName, setDebouncedFilterName] = useState('');
  const [filterStato, setFilterStato] = useState('');
  const [filterDossierStatuses, setFilterDossierStatuses] = useState([]);
  const [debouncedDossierStatuses, setDebouncedDossierStatuses] = useState([]);
  const [filterSesso, setFilterSesso] = useState('');
  const [filterEvaluation, setFilterEvaluation] = useState('');
  const [filterOrigin, setFilterOrigin] = useState('');
  const [filterStatoPreventivo, setFilterStatoPreventivo] = useState('');
  const [filterStatoQuestionario, setFilterStatoQuestionario] = useState('');
  const [filterProfessione, setFilterProfessione] = useState('');
  const [filterEtaDa, setFilterEtaDa] = useState('');
  const [filterEtaA, setFilterEtaA] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [showFilters, setShowFilters] = useState(false);
  const [total, setTotal] = useState(0);
  const [statiOptions, setStatiOptions] = useState([]);
  const [dossierStatusOptions, setDossierStatusOptions] = useState([]);
  const [genderOptions, setGenderOptions] = useState([]);
  const [evaluationOptions, setEvaluationOptions] = useState([]);
  const [originOptions, setOriginOptions] = useState([]);
  const [statiPreventivoOptions, setStatiPreventivoOptions] = useState([]);
  const [statiQuestionarioOptions, setStatiQuestionarioOptions] = useState([]);
  const [provinceOptions, setProvinceOptions] = useState([]);
  const [cityOptions, setCityOptions] = useState([]);
  const [professioniFiltroOptions, setProfessioniFiltroOptions] = useState([]);
  const [professioneOptions, setProfessioneOptions] = useState([]);
  
  // Export Dialog State
  const [exportDialogOpen, setExportDialogOpen] = useState(false);
  const [exportProvincia, setExportProvincia] = useState(null);
  const [exportCitta, setExportCitta] = useState(null);
  const [exportProfessione, setExportProfessione] = useState(null);
  const [importDialogOpen, setImportDialogOpen] = useState(false);
  const [pfOperationOptions, setPfOperationOptions] = useState([]);
  const [loadingPfOperationOptions, setLoadingPfOperationOptions] = useState(false);
  const [selectedPfOperationCodes, setSelectedPfOperationCodes] = useState([]);
  const [columnMenuAnchorEl, setColumnMenuAnchorEl] = useState(null);
  const [visibleColumns, setVisibleColumns] = useState(DEFAULT_VISIBLE_CUSTOMER_COLUMNS);

  // View Dialog State removed
  
  useEffect(() => {
    const handle = setTimeout(() => {
      setDebouncedFilterName(filterName.trim());
      setPage(0);
    }, 300);
    return () => clearTimeout(handle);
  }, [filterName]);

  useEffect(() => {
    const handle = setTimeout(() => {
      setDebouncedDossierStatuses(filterDossierStatuses);
      setPage(0);
    }, 300);
    return () => clearTimeout(handle);
  }, [filterDossierStatuses]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const params = {
        page: page + 1,
        limit: rowsPerPage,
        orderBy,
        order,
      };

      if (debouncedFilterName) params.search = debouncedFilterName;
      if (filterStato) params.stato = filterStato;
      if (debouncedDossierStatuses.length) params.dossierStatus = debouncedDossierStatuses.join(',');
      if (filterSesso) params.gender = filterSesso;
      if (filterEvaluation) params.evaluation = filterEvaluation;
      if (filterOrigin) params.origin = filterOrigin;
      if (filterStatoPreventivo) params.statoPreventivo = filterStatoPreventivo;
      if (filterStatoQuestionario) params.statoQuestionario = filterStatoQuestionario;
      if (filterProfessione) params.professione = filterProfessione;
      if (filterEtaDa !== '' && Number.isFinite(Number(filterEtaDa))) params.etaDa = Number(filterEtaDa);
      if (filterEtaA !== '' && Number.isFinite(Number(filterEtaA))) params.etaA = Number(filterEtaA);

      const res = await api.get('/customer', { params });
      setList(res.data.data || []);
      setTotal(res.data.total || 0);
    } catch (error) {
      console.error('Error fetching customer list:', error);
      toast.error('Errore nel caricamento dei dati Customer');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [
    page,
    rowsPerPage,
    order,
    orderBy,
    debouncedFilterName,
    filterStato,
    debouncedDossierStatuses,
    filterSesso,
    filterEvaluation,
    filterOrigin,
    filterStatoPreventivo,
    filterStatoQuestionario,
    filterProfessione,
    filterEtaDa,
    filterEtaA,
  ]);

  useEffect(() => {
    const fetchFilterOptions = async () => {
      try {
        const res = await api.get('/customer/filters/options');
        const data = res.data || {};
        setStatiOptions((data.stati || []).filter(Boolean));
        setDossierStatusOptions((data.dossierStatuses || []).filter(Boolean));
        setGenderOptions((data.genders || []).filter(Boolean));
        setEvaluationOptions((data.evaluations || []).filter(Boolean));
        setOriginOptions((data.origins || []).filter(Boolean));
        setStatiPreventivoOptions((data.statiPreventivo || []).filter(Boolean));
        setStatiQuestionarioOptions((data.statiQuestionario || []).filter(Boolean));
        setProvinceOptions(data.province || []);
        setCityOptions(data.cities || []);
        setProfessioniFiltroOptions(data.professioni || []);
      } catch (error) {
        console.error('Error fetching customer filter options:', error);
      }
    };

    fetchFilterOptions();
  }, []);

  const fetchExportOptions = async (currentFilters = {}) => {
    try {
      const filters = {
        search: filterName,
        stato: filterStato,
        dossierStatus: filterDossierStatuses,
        gender: filterSesso,
        evaluation: filterEvaluation,
        origin: filterOrigin,
        statoPreventivo: filterStatoPreventivo,
        statoQuestionario: filterStatoQuestionario,
        provincia: currentFilters.provincia || exportProvincia?.label || '',
        citta: currentFilters.citta || exportCitta?.label || ''
      };

      const response = await api.post('/customer/export/options', filters);
      const data = response.data;
      
      setProvinceOptions(data.province || []);
      setCityOptions(data.cities || []);
      setProfessioneOptions(data.professioni || []);
    } catch (error) {
      console.error('Error fetching export options:', error);
    }
  };

  // Fetch export options when dialog opens
  useEffect(() => {
    if (exportDialogOpen) {
      fetchExportOptions();
    }
  }, [exportDialogOpen]);

  // Fetch export options when province or city changes
  useEffect(() => {
    if (exportDialogOpen) {
      fetchExportOptions({
        provincia: exportProvincia?.label || '',
        citta: exportCitta?.label || ''
      });
    }
  }, [exportProvincia, exportCitta]);

  useEffect(() => {
    if (!importDialogOpen) return;

    const fetchPfOperationOptions = async () => {
      setLoadingPfOperationOptions(true);
      try {
        const response = await api.get('/pf-tipi-operazione');
        const rows = Array.isArray(response.data) ? response.data : [];
        const nextOptions = rows
          .filter((row) => Number(row?.attivo) !== 0)
          .map((row) => ({
            id: Number(row.id),
            code: String(row.codice || '').trim(),
            label: String(row.nome_visualizzato || row.codice || '').trim(),
            description: String(row.descrizione || '').trim(),
            richiedeTfs: Number(row.richiede_tfs) ? 1 : 0
          }))
          .filter((row) => row.code);

        setPfOperationOptions(nextOptions);
        setSelectedPfOperationCodes((current) => {
          const validCurrent = current.filter((code) => nextOptions.some((option) => option.code === code));
          if (validCurrent.length) return validCurrent;
          const preferred = nextOptions.find((option) => option.code === 'cessione_quinto');
          if (preferred) return [preferred.code];
          return nextOptions[0]?.code ? [nextOptions[0].code] : [];
        });
      } catch (error) {
        console.error('Error fetching PF operation options:', error);
        toast.error('Errore nel caricamento dei tipi pratica');
        setPfOperationOptions([]);
        setSelectedPfOperationCodes([]);
      } finally {
        setLoadingPfOperationOptions(false);
      }
    };

    fetchPfOperationOptions();
  }, [importDialogOpen]);

  const handleOpenExportDialog = () => {
    setExportProvincia(null);
    setExportCitta(null);
    setExportProfessione(null);
    setExportDialogOpen(true);
  };

  const handleExport = async () => {
    setExporting(true);
    setExportDialogOpen(false);
    try {
      const filters = {
        search: filterName,
        stato: filterStato,
        gender: filterSesso,
        evaluation: filterEvaluation,
        origin: filterOrigin,
        statoPreventivo: filterStatoPreventivo,
        statoQuestionario: filterStatoQuestionario,
        provincia: exportProvincia?.label || '',
        citta: exportCitta?.label || '',
        professione: exportProfessione?.label || ''
      };

      const response = await api.post('/customer/export', filters);
      
      if (response.data.jobId) {
        addJob(response.data.jobId, 'customer_csv');
        toast.info('Esportazione avviata. Riceverai una notifica al completamento.');
      }
    } catch (error) {
      console.error('Export error:', error);
      toast.error('Errore durante l\'avvio dell\'esportazione');
    } finally {
      setExporting(false);
    }
  };

  const handleImportPfClienti = async () => {
    if (!selectedPfOperationCodes.length) {
      toast.error('Seleziona almeno un tipo pratica da creare o aggiornare');
      return;
    }

    setImportingPfClienti(true);
    try {
      const payload = {
        search: filterName,
        stato: filterStato,
        dossierStatus: filterDossierStatuses,
        gender: filterSesso,
        evaluation: filterEvaluation,
        origin: filterOrigin,
        statoPreventivo: filterStatoPreventivo,
        statoQuestionario: filterStatoQuestionario,
        professione: filterProfessione,
        etaDa: filterEtaDa !== '' && Number.isFinite(Number(filterEtaDa)) ? Number(filterEtaDa) : '',
        etaA: filterEtaA !== '' && Number.isFinite(Number(filterEtaA)) ? Number(filterEtaA) : '',
        tipoOperazioneCodici: selectedPfOperationCodes,
      };

      const res = await api.post('/pf-clienti/assign', payload);
      const jobId = res?.data?.jobId;
      if (!jobId) {
        toast.error('Impossibile avviare l’assegnazione: jobId mancante');
        return;
      }
      addJob(jobId, 'pf_clienti_assign', { silent: true });
      toast.info('Assegnazione clienti avviata. Ti aggiorniamo al completamento nelle notifiche.');
      setImportDialogOpen(false);
    } catch (error) {
      console.error('Import PF Clienti error:', error);
      toast.error('Errore durante l\'avvio dell\'assegnazione clienti');
    } finally {
      setImportingPfClienti(false);
    }
  };

  const handleOpenImportDialog = () => {
    setImportDialogOpen(true);
  };

  const handleRequestSort = (property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const handleFilterByName = (event) => {
    setFilterName(event.target.value);
  };

  const handleFilterByStato = (event) => {
    setFilterStato(event.target.value);
    setPage(0);
  };

  const handleFilterBySesso = (event) => {
    setFilterSesso(event.target.value);
    setPage(0);
  };

  const handleFilterByEvaluation = (event) => {
    setFilterEvaluation(event.target.value);
    setPage(0);
  };

  const handleFilterByOrigin = (event) => {
    setFilterOrigin(event.target.value);
    setPage(0);
  };

  const handleFilterByStatoPreventivo = (event) => {
    setFilterStatoPreventivo(event.target.value);
    setPage(0);
  };

  const handleFilterByStatoQuestionario = (event) => {
    setFilterStatoQuestionario(event.target.value);
    setPage(0);
  };

  const handleFilterByProfessione = (event) => {
    setFilterProfessione(event.target.value);
    setPage(0);
  };

  const handleFilterByEtaDa = (event) => {
    setFilterEtaDa(event.target.value);
    setPage(0);
  };

  const handleFilterByEtaA = (event) => {
    setFilterEtaA(event.target.value);
    setPage(0);
  };

  const handleView = (id) => {
    navigate(`/customer/${id}`);
  };

  const getStatusColor = (status) => {
    if (!status) return 'default';
    const lower = status.toLowerCase();
    
    // Success (Green) - Positive outcomes
    if (['completato', 'confermato', 'risolto', 'pagato', 'attivo', 'success'].some(k => lower.includes(k))) return 'success';
    
    // Error (Red) - Negative outcomes or critical states
    if (['errore', 'annullato', 'bloccato', 'rifiutato', 'chiuso', 'non risponde', 'irraggiungibile'].some(k => lower.includes(k))) return 'error';
    
    // Warning (Orange) - Pending or requiring action
    if (['in attesa', 'pending', 'da verificare', 'sospeso', 'da contattare', 'richiamare', 'occupato'].some(k => lower.includes(k))) return 'warning';
    
    // Info (Blue) - Neutral or initial states
    if (['nuovo', 'bozza', 'in lavorazione', 'aperto', 'inviato', 'letto'].some(k => lower.includes(k))) return 'info';
    
    return 'default';
  };

  const getAge = (birthdate) => {
    if (!birthdate) return null;
    const date = new Date(birthdate);
    if (Number.isNaN(date.getTime())) return null;
    const now = new Date();
    let age = now.getFullYear() - date.getFullYear();
    const monthDiff = now.getMonth() - date.getMonth();
    if (monthDiff < 0 || (monthDiff === 0 && now.getDate() < date.getDate())) {
      age -= 1;
    }
    return age;
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const activeFiltersCount = [
    !!filterStato,
    filterDossierStatuses.length > 0,
    !!filterSesso,
    !!filterEvaluation,
    !!filterOrigin,
    !!filterStatoPreventivo,
    !!filterStatoQuestionario,
    !!filterProfessione,
    filterEtaDa !== '',
    filterEtaA !== '',
  ].filter(Boolean).length;

  const sortedList = [...list].sort((a, b) => {
    const isAsc = order === 'asc';
    if (orderBy === 'id') {
      return isAsc ? a[orderBy] - b[orderBy] : b[orderBy] - a[orderBy];
    }
    const valA = a[orderBy] ? a[orderBy].toString().toLowerCase() : '';
    const valB = b[orderBy] ? b[orderBy].toString().toLowerCase() : '';
    if (valA < valB) return isAsc ? -1 : 1;
    if (valA > valB) return isAsc ? 1 : -1;
    return 0;
  });

  const displayedColumns = CUSTOMER_COLUMN_DEFS.filter((column) => visibleColumns.includes(column.key));
  const selectedPfOperationOptions = pfOperationOptions.filter((option) => selectedPfOperationCodes.includes(option.code));
  const dossierStatusLabel = (value) => (value === '__NO_DOSSIER__' ? 'Senza dossier' : value);
  const professioneLabel = (value) => (value === '__NO_PROFESSION__' ? 'Senza professione' : value);

  const handleOpenColumnMenu = (event) => {
    setColumnMenuAnchorEl(event.currentTarget);
  };

  const handleCloseColumnMenu = () => {
    setColumnMenuAnchorEl(null);
  };

  const handleToggleColumn = (columnKey) => {
    setVisibleColumns((current) =>
      current.includes(columnKey) ? current.filter((key) => key !== columnKey) : [...current, columnKey]
    );
  };

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Box
            sx={{
              width: 40,
              height: 40,
              borderRadius: '50%',
              bgcolor: '#eef2ff',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            <CustomersLogoIcon size={20} color="#4f46e5" />
          </Box>
          <Box>
            <Typography variant="h4" sx={{ fontWeight: 'bold' }}>
              Clienti (Customer Remoto)
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Anagrafica clienti remota con stato commerciale e filtri avanzati
            </Typography>
          </Box>
        </Box>
        {(canAssignClienti || isAdmin) && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {canAssignClienti && (
              <Button
                variant="contained"
                onClick={handleOpenImportDialog}
                disabled={importingPfClienti || loading}
                startIcon={
                  importingPfClienti ? <CircularProgress size={20} color="inherit" /> : <CustomersLogoIcon size={20} />
                }
              >
                {importingPfClienti ? 'Assegnazione...' : 'Assegna Clienti'}
              </Button>
            )}
            {isAdmin && (
              <Button
                variant="outlined"
                startIcon={exporting ? <CircularProgress size={20} color="inherit" /> : <DownloadIcon size={20} />}
                onClick={handleOpenExportDialog}
                disabled={exporting}
              >
                {exporting ? 'Esportazione...' : 'Esporta'}
              </Button>
            )}
          </Box>
        )}
      </Box>

      <StyledCard>
        <Box sx={{ p: 2 }}>
          <TextField
            fullWidth
            placeholder="Cerca per nome, email, telefono..."
            value={filterName}
            onChange={handleFilterByName}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search size={20} />
                </InputAdornment>
              ),
              endAdornment: (
                <InputAdornment position="end">
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    {filterName && (
                      <IconButton size="small" onClick={() => setFilterName('')}>
                        <X size={18} />
                      </IconButton>
                    )}
                    <IconButton onClick={() => setShowFilters(!showFilters)}>
                      <Badge badgeContent={activeFiltersCount} color="primary">
                        <FilterAltIcon color={showFilters || activeFiltersCount > 0 ? 'primary' : 'action'} />
                      </Badge>
                    </IconButton>
                    <Tooltip title="Colonne">
                      <IconButton onClick={handleOpenColumnMenu}>
                        <ViewColumnIcon color={visibleColumns.length !== CUSTOMER_COLUMN_DEFS.length ? 'primary' : 'action'} />
                      </IconButton>
                    </Tooltip>
                  </Box>
                </InputAdornment>
              ),
            }}
          />
        </Box>

        {showFilters && (
          <Box sx={{ px: 2, pb: 2 }}>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6} md={3}>
                <FormControl fullWidth>
                  <InputLabel>Stato</InputLabel>
                  <Select
                    value={filterStato}
                    label="Stato"
                    onChange={handleFilterByStato}
                  >
                    <MenuItem value=""><em>Tutti</em></MenuItem>
                    {statiOptions.map((option) => (
                      <MenuItem key={option} value={option}>{option}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Autocomplete
                  multiple
                  options={dossierStatusOptions}
                  getOptionLabel={(option) => dossierStatusLabel(option)}
                  value={filterDossierStatuses}
                  onChange={(event, value) => {
                    setFilterDossierStatuses(value);
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Status Dossier"
                      placeholder="Tutti"
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <FormControl fullWidth>
                  <InputLabel>Sesso</InputLabel>
                  <Select
                    value={filterSesso}
                    label="Sesso"
                    onChange={handleFilterBySesso}
                  >
                    <MenuItem value=""><em>Tutti</em></MenuItem>
                    {genderOptions.map((gender) => (
                      <MenuItem key={gender} value={gender}>
                        {gender}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              
              <Grid item xs={12} sm={6} md={3}>
                <FormControl fullWidth>
                  <InputLabel>Evaluation</InputLabel>
                  <Select
                    value={filterEvaluation}
                    label="Evaluation"
                    onChange={handleFilterByEvaluation}
                  >
                    <MenuItem value=""><em>Tutti</em></MenuItem>
                    {evaluationOptions.map((option) => (
                      <MenuItem key={option} value={option}>{option}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12} sm={6} md={3}>
                <FormControl fullWidth>
                  <InputLabel>Origin</InputLabel>
                  <Select
                    value={filterOrigin}
                    label="Origin"
                    onChange={handleFilterByOrigin}
                  >
                    <MenuItem value=""><em>Tutti</em></MenuItem>
                    {originOptions.map((option) => (
                      <MenuItem key={option} value={option}>{option}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12} sm={6} md={3}>
                <FormControl fullWidth>
                  <InputLabel>Stato Preventivo</InputLabel>
                  <Select
                    value={filterStatoPreventivo}
                    label="Stato Preventivo"
                    onChange={handleFilterByStatoPreventivo}
                  >
                    <MenuItem value=""><em>Tutti</em></MenuItem>
                    {statiPreventivoOptions.map((option) => (
                      <MenuItem key={option} value={option}>{option}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12} sm={6} md={3}>
                <FormControl fullWidth>
                  <InputLabel>Stato Questionario</InputLabel>
                  <Select
                    value={filterStatoQuestionario}
                    label="Stato Questionario"
                    onChange={handleFilterByStatoQuestionario}
                  >
                    <MenuItem value=""><em>Tutti</em></MenuItem>
                    {statiQuestionarioOptions.map((option) => (
                      <MenuItem key={option} value={option}>{option}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12} sm={6} md={3}>
                <FormControl fullWidth>
                  <InputLabel>Professione</InputLabel>
                  <Select
                    value={filterProfessione}
                    label="Professione"
                    onChange={handleFilterByProfessione}
                  >
                    <MenuItem value=""><em>Tutte</em></MenuItem>
                    {professioniFiltroOptions.map((opt) => (
                      <MenuItem key={opt.label} value={opt.label}>
                        {professioneLabel(opt.label)}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <TextField
                    fullWidth
                    label="Età da"
                    type="number"
                    value={filterEtaDa}
                    onChange={handleFilterByEtaDa}
                    inputProps={{ min: 0 }}
                  />
                  <TextField
                    fullWidth
                    label="Età a"
                    type="number"
                    value={filterEtaA}
                    onChange={handleFilterByEtaA}
                    inputProps={{ min: 0 }}
                  />
                </Box>
              </Grid>
            </Grid>
          </Box>
        )}

        <TableContainer>
          <Table>
            <TableHead>
              <TableRow sx={{ bgcolor: 'background.neutral' }}>
                {visibleColumns.includes('id') && (
                <TableCell>
                  <TableSortLabel
                    active={orderBy === 'id'}
                    direction={orderBy === 'id' ? order : 'asc'}
                    onClick={() => handleRequestSort('id')}
                  >
                    ID
                  </TableSortLabel>
                </TableCell>
                )}
                {visibleColumns.includes('stato_comunicazione') && (
                <TableCell>
                  <TableSortLabel
                    active={orderBy === 'stato_comunicazione'}
                    direction={orderBy === 'stato_comunicazione' ? order : 'asc'}
                    onClick={() => handleRequestSort('stato_comunicazione')}
                  >
                    Stato
                  </TableSortLabel>
                </TableCell>
                )}
                {visibleColumns.includes('surname') && (
                <TableCell>
                  <TableSortLabel
                    active={orderBy === 'surname'}
                    direction={orderBy === 'surname' ? order : 'asc'}
                    onClick={() => handleRequestSort('surname')}
                  >
                    Cognome
                  </TableSortLabel>
                </TableCell>
                )}
                {visibleColumns.includes('name') && (
                <TableCell>
                  <TableSortLabel
                    active={orderBy === 'name'}
                    direction={orderBy === 'name' ? order : 'asc'}
                    onClick={() => handleRequestSort('name')}
                  >
                    Nome
                  </TableSortLabel>
                </TableCell>
                )}
                {visibleColumns.includes('gender') && (
                <TableCell>
                  <TableSortLabel
                    active={orderBy === 'gender'}
                    direction={orderBy === 'gender' ? order : 'asc'}
                    onClick={() => handleRequestSort('gender')}
                  >
                    Sesso
                  </TableSortLabel>
                </TableCell>
                )}
                {visibleColumns.includes('data_nascita') && (
                <TableCell>
                  <TableSortLabel
                    active={orderBy === 'data_nascita'}
                    direction={orderBy === 'data_nascita' ? order : 'asc'}
                    onClick={() => handleRequestSort('data_nascita')}
                  >
                    Data Nascita
                  </TableSortLabel>
                </TableCell>
                )}
                {visibleColumns.includes('codice_fiscale') && (
                <TableCell>
                  <TableSortLabel
                    active={orderBy === 'codice_fiscale'}
                    direction={orderBy === 'codice_fiscale' ? order : 'asc'}
                    onClick={() => handleRequestSort('codice_fiscale')}
                  >
                    Codice Fiscale
                  </TableSortLabel>
                </TableCell>
                )}
                {visibleColumns.includes('email') && (
                <TableCell>
                  <TableSortLabel
                    active={orderBy === 'email'}
                    direction={orderBy === 'email' ? order : 'asc'}
                    onClick={() => handleRequestSort('email')}
                  >
                    Email
                  </TableSortLabel>
                </TableCell>
                )}
                {visibleColumns.includes('telephone') && (
                <TableCell>
                  <TableSortLabel
                    active={orderBy === 'telephone'}
                    direction={orderBy === 'telephone' ? order : 'asc'}
                    onClick={() => handleRequestSort('telephone')}
                  >
                    Telefono
                  </TableSortLabel>
                </TableCell>
                )}
                <TableCell align="center">Azioni</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={displayedColumns.length + 1} align="center" sx={{ py: 3 }}>
                    <LoaderProgress size={50} />
                  </TableCell>
                </TableRow>
              ) : sortedList.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={displayedColumns.length + 1} align="center" sx={{ py: 3 }}>
                    <Typography variant="body1" color="text.secondary">
                      Nessun dato trovato
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                sortedList.map((item) => (
                  <TableRow key={item.id} hover>
                    {visibleColumns.includes('id') && <TableCell>{item.id}</TableCell>}
                    {visibleColumns.includes('stato_comunicazione') && (
                    <TableCell>
                      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 0.5, minWidth: 0 }}>
                        <Chip 
                          label={item.stato_comunicazione} 
                          color={getStatusColor(item.stato_comunicazione)} 
                          size="small" 
                          variant="outlined" 
                          sx={{ fontWeight: 'bold' }}
                        />
                        {item?.pf_pratica_badge?.tipo_operazione_nome && item?.pf_pratica_badge?.stato_nome ? (
                          <Tooltip
                            title={`${item.pf_pratica_badge.tipo_operazione_nome} · ${item.pf_pratica_badge.stato_nome}`}
                            placement="top"
                          >
                            <Typography variant="caption" color="text.secondary" noWrap sx={{ maxWidth: 180 }}>
                              {item.pf_pratica_badge.tipo_operazione_nome} · {item.pf_pratica_badge.stato_nome}
                            </Typography>
                          </Tooltip>
                        ) : null}
                      </Box>
                    </TableCell>
                    )}
                    {visibleColumns.includes('surname') && <TableCell>{item.surname}</TableCell>}
                    {visibleColumns.includes('name') && <TableCell>{item.name}</TableCell>}
                    {visibleColumns.includes('gender') && <TableCell>{item.gender || '-'}</TableCell>}
                    {visibleColumns.includes('data_nascita') && (
                    <TableCell>
                      {item.data_nascita ? (
                        (() => {
                          const age = getAge(item.data_nascita);
                          const isMinor = age !== null && age < 18;
                          return (
                            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
                              <Typography variant="body2">
                                {new Date(item.data_nascita).toLocaleDateString()}
                              </Typography>
                              {age !== null && (
                                <Box
                                  sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 0.5,
                                    mt: 0.25,
                                    color: isMinor ? 'error.main' : 'success.main',
                                  }}
                                >
                                  <User size={14} />
                                  <Typography
                                    variant="caption"
                                    sx={{
                                      fontWeight: isMinor ? 600 : 400,
                                    }}
                                  >
                                    {age} anni
                                  </Typography>
                                </Box>
                              )}
                            </Box>
                          );
                        })()
                      ) : (
                        '-'
                      )}
                    </TableCell>
                    )}
                    {visibleColumns.includes('codice_fiscale') && <TableCell>{item.codice_fiscale || '-'}</TableCell>}
                    {visibleColumns.includes('email') && <TableCell>{item.email}</TableCell>}
                    {visibleColumns.includes('telephone') && <TableCell>{item.telephone}</TableCell>}
                    <TableCell align="center">
                      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 1.5 }}>
                        <Tooltip title="Totale dossier collegati al cliente" arrow>
                          <Badge
                            badgeContent={item.total_dossier || 0}
                            color="primary"
                            overlap="circular"
                            anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
                          >
                            <Box
                              sx={{
                                width: 28,
                                height: 28,
                                borderRadius: '50%',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                bgcolor: 'rgba(145, 158, 171, 0.08)',
                                color: 'text.secondary',
                              }}
                            >
                              <FileText size={16} />
                            </Box>
                          </Badge>
                        </Tooltip>
                        <Tooltip title="Totale negotiation collegate ai dossier del cliente" arrow>
                          <Badge
                            badgeContent={item.total_negotiation || 0}
                            color="secondary"
                            overlap="circular"
                            anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
                          >
                            <Box
                              sx={{
                                width: 28,
                                height: 28,
                                borderRadius: '50%',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                bgcolor: 'rgba(145, 158, 171, 0.08)',
                                color: 'text.secondary',
                              }}
                            >
                              <TrendingUp size={16} />
                            </Box>
                          </Badge>
                        </Tooltip>
                        <IconButton onClick={() => handleView(item.id)} color="primary">
                          <Eye size={20} />
                        </IconButton>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>

        <TablePagination
          rowsPerPageOptions={[10, 25, 50]}
          component="div"
          count={total}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          labelRowsPerPage="Righe per pagina:"
        />
      </StyledCard>
      <Menu
        anchorEl={columnMenuAnchorEl}
        open={Boolean(columnMenuAnchorEl)}
        onClose={handleCloseColumnMenu}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        {CUSTOMER_COLUMN_DEFS.map((column) => (
          <MenuItem key={column.key} onClick={() => handleToggleColumn(column.key)} dense>
            <FormControlLabel
              control={<Checkbox size="small" checked={visibleColumns.includes(column.key)} />}
              label={column.label}
              sx={{ mr: 0, width: '100%', pointerEvents: 'none' }}
            />
          </MenuItem>
        ))}
      </Menu>
      <Dialog open={exportDialogOpen} onClose={() => setExportDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Esporta Clienti</DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Typography variant="body2" color="text.secondary">
              Seleziona filtri opzionali per l'esportazione:
            </Typography>
            
            <Autocomplete
              value={exportProvincia}
              onChange={(event, newValue) => {
                setExportProvincia(newValue);
              }}
              options={provinceOptions}
              getOptionLabel={(option) => option.label || ''}
              isOptionEqualToValue={(option, value) => option.label === value.label}
              renderOption={(props, option) => (
                <li {...props}>
                  {option.label} ({option.count})
                </li>
              )}
              renderInput={(params) => <TextField {...params} label="Provincia Residenza" size="small" />}
              fullWidth
            />

            <Autocomplete
              value={exportCitta}
              onChange={(event, newValue) => {
                setExportCitta(newValue);
              }}
              options={cityOptions}
              getOptionLabel={(option) => option.label || ''}
              isOptionEqualToValue={(option, value) => option.label === value.label}
              renderOption={(props, option) => (
                <li {...props}>
                  {option.label} ({option.count})
                </li>
              )}
              renderInput={(params) => <TextField {...params} label="Città Residenza" size="small" />}
              fullWidth
            />

            <Autocomplete
              value={exportProfessione}
              onChange={(event, newValue) => {
                setExportProfessione(newValue);
              }}
              options={professioneOptions}
              getOptionLabel={(option) => option.label || ''}
              isOptionEqualToValue={(option, value) => option.label === value.label}
              renderOption={(props, option) => (
                <li {...props}>
                  {option.label} ({option.count})
                </li>
              )}
              renderInput={(params) => <TextField {...params} label="Professione" size="small" />}
              fullWidth
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setExportDialogOpen(false)}>Annulla</Button>
          <Button onClick={handleExport} variant="contained" color="primary">
            Conferma Esportazione
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog open={importDialogOpen} onClose={() => setImportDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Assegna a valutazione</DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Typography variant="body2" color="text.secondary">
              Questo comando prepara i nominativi del Customer Remoto per la valutazione in “Pratiche Finanziarie”, collegandoli ai tipi
              pratica selezionati.
            </Typography>

            <Alert
              severity="info"
              sx={{
                borderRadius: 2,
                alignItems: 'flex-start',
                '& .MuiAlert-message': { width: '100%' }
              }}
            >
              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                L&apos;assegnazione verrà applicata a <strong>{Number(total || 0).toLocaleString('it-IT')}</strong>{' '}
                {Number(total || 0) === 1 ? 'nominativo filtrato' : 'nominativi filtrati'}.
              </Typography>
            </Alert>

            <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'flex-start' }}>
              <Box
                sx={{
                  width: 34,
                  height: 34,
                  borderRadius: 1.5,
                  bgcolor: 'rgba(34, 197, 94, 0.12)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0
                }}
              >
                <ShieldCheck size={18} color="#16a34a" />
              </Box>
              <Box>
                <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                  Regole di creazione pratica
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Prima della conferma, verifica come il sistema gestisce le pratiche già presenti per lo stesso cliente e tipo operazione.
                </Typography>
                <Alert
                  severity="info"
                  sx={{
                    mt: 1.25,
                    borderRadius: 2,
                    alignItems: 'flex-start',
                    '& .MuiAlert-message': { width: '100%' }
                  }}
                >
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.2 }}>
                    <Box>
                      <Typography variant="body2" sx={{ fontWeight: 700 }}>
                        Bozza già presente
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Se esiste una bozza dello stesso tipo, viene aggiornata e riutilizzata.
                      </Typography>
                    </Box>

                    <Box>
                      <Typography variant="body2" sx={{ fontWeight: 700 }}>
                        Pratica precedente chiusa
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Se la pratica dello stesso tipo è finale o chiusa, viene creata una nuova pratica.
                      </Typography>
                    </Box>

                    <Box>
                      <Typography variant="body2" sx={{ fontWeight: 700 }}>
                        Pratica già attiva
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Se esiste una pratica aperta dello stesso tipo, non viene creata una nuova pratica automatica.
                      </Typography>
                    </Box>

                    <Box>
                      <Typography variant="body2" sx={{ fontWeight: 700 }}>
                        Tipi che richiedono TFS
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        La creazione avviene solo per clienti idonei in base ai dati disponibili.
                      </Typography>
                    </Box>
                  </Box>
                </Alert>
              </Box>
            </Box>

            <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'flex-start' }}>
              <Box
                sx={{
                  width: 34,
                  height: 34,
                  borderRadius: 1.5,
                  bgcolor: 'rgba(79, 70, 229, 0.12)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0
                }}
              >
                <CustomersLogoIcon size={18} color="#4f46e5" />
              </Box>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.5 }}>
                  Quale pratica vuoi creare o aggiornare?
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  Puoi selezionare uno o più tipi pratica. Per ogni cliente filtrato verrà creata o aggiornata la relativa bozza.
                </Typography>
                <Autocomplete
                  multiple
                  disableCloseOnSelect
                  options={pfOperationOptions}
                  loading={loadingPfOperationOptions}
                  value={selectedPfOperationOptions}
                  onChange={(event, newValue) => {
                    setSelectedPfOperationCodes(newValue.map((option) => option.code));
                  }}
                  getOptionLabel={(option) => option.label || option.code || ''}
                  isOptionEqualToValue={(option, value) => option.code === value.code}
                  noOptionsText={loadingPfOperationOptions ? 'Caricamento...' : 'Nessun tipo pratica disponibile'}
                  renderOption={(props, option, { selected }) => (
                    <li {...props}>
                      <Checkbox checked={selected} size="small" sx={{ mr: 1 }} />
                      <Box sx={{ display: 'flex', flexDirection: 'column', minWidth: 0 }}>
                        <Typography variant="body2" sx={{ fontWeight: 600, color: 'text.primary' }}>
                          {option.label}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {option.description || (option.richiedeTfs ? 'Disponibile per clienti idonei TFS.' : 'Tipo pratica disponibile per l’assegnazione.')}
                        </Typography>
                      </Box>
                    </li>
                  )}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Tipi pratica"
                      placeholder={selectedPfOperationOptions.length ? '' : 'Seleziona uno o più tipi pratica'}
                    />
                  )}
                  fullWidth
                />
                {!selectedPfOperationCodes.length && (
                  <Typography variant="caption" color="error.main" sx={{ display: 'block', mt: 0.75 }}>
                    Seleziona almeno un tipo pratica per procedere.
                  </Typography>
                )}
              </Box>
            </Box>

            <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'flex-start' }}>
              <Box
                sx={{
                  width: 34,
                  height: 34,
                  borderRadius: 1.5,
                  bgcolor: 'rgba(245, 158, 11, 0.12)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0
                }}
              >
                <Mail size={18} color="#f59e0b" />
              </Box>
              <Box>
                <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                  Notifica via email
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Invia una email per segnalare inserimento nuova richiesta valutazione posizione Clienti.
                </Typography>
              </Box>
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setImportDialogOpen(false)} disabled={importingPfClienti}>
            Annulla
          </Button>
          <Button
            onClick={handleImportPfClienti}
            variant="contained"
            color="primary"
            disabled={importingPfClienti || loadingPfOperationOptions || !selectedPfOperationCodes.length}
          >
            {importingPfClienti ? 'Assegnazione...' : 'Assegna a valutazione'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
